import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Lock, Check, Palette, Eye } from "lucide-react";
import { SKINS, getCurrentSkin, setCurrentSkinId } from "@/lib/skins";
import { sfx } from "@/lib/sfx";

// Small animated character preview — mimics the in-game fugitive sprite
function SkinPreview({ skin }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    let raf = 0;
    let t = 0;
    const draw = () => {
      t += 0.02;
      ctx.clearRect(0, 0, W, H);
      // Ground grid
      ctx.strokeStyle = "rgba(0,240,255,0.10)";
      ctx.lineWidth = 1;
      for (let i = 0; i < 8; i++) {
        const y = 12 + i * 14;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }
      // Character — animated bob + glow
      const bob = Math.sin(t * 4) * 3;
      const cx = W / 2;
      const cy = H / 2 + bob;
      // Glow halo
      const grad = ctx.createRadialGradient(cx, cy, 4, cx, cy, 34);
      grad.addColorStop(0, skin.glow);
      grad.addColorStop(1, skin.glow.replace(/[\d.]+\)$/, "0)"));
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, 34, 0, Math.PI * 2);
      ctx.fill();
      // Body
      ctx.fillStyle = skin.color;
      ctx.fillRect(cx - 8, cy - 8, 16, 16);
      // Head
      ctx.fillStyle = "#0a0a0f";
      ctx.fillRect(cx - 5, cy - 12, 10, 4);
      // Motion streak behind (running to the right)
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = skin.color;
      ctx.fillRect(cx - 18 - Math.sin(t * 6) * 2, cy - 4, 6, 2);
      ctx.fillRect(cx - 26 - Math.sin(t * 6 + 1) * 2, cy, 4, 2);
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [skin]);
  return (
    <canvas
      ref={canvasRef}
      width={160}
      height={110}
      className="w-40 h-28 block bg-black/60 border"
      style={{ borderColor: skin.color, boxShadow: `0 0 22px ${skin.glow}` }}
    />
  );
}

export default function SkinPicker({ open, onOpenChange, onSelect }) {
  const [current, setCurrent] = useState(() => getCurrentSkin());
  const [previewSkin, setPreviewSkin] = useState(() => getCurrentSkin());

  const choose = (skin) => {
    if (!skin.unlock()) return;
    sfx.click();
    const applied = setCurrentSkinId(skin.id);
    setCurrent(applied);
    onSelect && onSelect(applied);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="skin-picker-dialog"
        className="max-w-2xl bg-black/80 backdrop-blur-2xl border border-white/10 text-white rounded-none"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tighter flex items-center gap-3">
            <Palette size={20} className="text-[#FFD400]" />
            <span>PICK YOUR <span className="text-cyan-300">SKIN</span></span>
          </DialogTitle>
        </DialogHeader>

        <div className="text-[10px] tracking-[0.3em] text-white/50">
          NEON PALETTE · HOVER TO PREVIEW · UNLOCK BY SURVIVING
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          {/* Preview panel */}
          <div className="md:col-span-1 flex flex-col items-center gap-3 p-4 border border-white/10 bg-black/30" data-testid="skin-preview-panel">
            <div className="text-[10px] tracking-[0.4em] text-white/50 flex items-center gap-2"><Eye size={12} /> PREVIEW</div>
            <SkinPreview skin={previewSkin} />
            <div className="text-center">
              <div className="text-xs tracking-[0.3em] font-bold" style={{ color: previewSkin.color }}>{previewSkin.name}</div>
              <div className="text-[10px] tracking-[0.25em] text-white/40 mt-0.5">{previewSkin.unlock() ? "UNLOCKED" : previewSkin.hint}</div>
            </div>
          </div>
          {/* List */}
          <div className="md:col-span-2 grid grid-cols-1 gap-2 max-h-[420px] overflow-y-auto pr-1">
            {SKINS.map((s) => {
              const unlocked = s.unlock();
              const active = current.id === s.id;
              return (
                <button
                  key={s.id}
                  data-testid={`skin-${s.id}`}
                  onClick={() => choose(s)}
                  onMouseEnter={() => setPreviewSkin(s)}
                  onFocus={() => setPreviewSkin(s)}
                  className="flex items-center gap-4 px-4 py-3 border transition-all text-left"
                  style={{
                    borderColor: active ? s.color : unlocked ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.08)",
                    background: active ? "rgba(0,0,0,0.6)" : "transparent",
                    boxShadow: active ? `0 0 20px ${s.glow}` : "none",
                    cursor: unlocked ? "pointer" : "not-allowed",
                    opacity: unlocked ? 1 : 0.5,
                  }}
                >
                  <div
                    className="w-8 h-8 shrink-0"
                    style={{
                      background: s.color,
                      boxShadow: `0 0 14px ${s.glow}`,
                      borderRadius: 2,
                    }}
                  />
                  <div className="flex-1">
                    <div className="text-xs tracking-[0.3em] font-bold" style={{ color: unlocked ? s.color : "rgba(255,255,255,0.4)" }}>
                      {s.name}
                    </div>
                    <div className="text-[10px] tracking-[0.25em] text-white/40 mt-0.5">
                      {unlocked ? "UNLOCKED" : s.hint}
                    </div>
                  </div>
                  {!unlocked ? (
                    <Lock size={14} className="text-white/40" />
                  ) : active ? (
                    <Check size={16} style={{ color: s.color }} />
                  ) : (
                    <span className="text-[10px] tracking-[0.3em] text-white/50">SELECT</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
